import { createContext, useContext, useRef, useCallback } from "react";

interface AudioManagerContextType {
  registerAudio: (trackId: number, audioElement: HTMLAudioElement) => void;
  unregisterAudio: (trackId: number) => void;
  stopAllExcept: (trackId: number) => void;
  stopAll: () => void;
}

const AudioManagerContext = createContext<AudioManagerContextType | null>(null);

export function AudioManagerProvider({ children }: { children: React.ReactNode }) {
  const audioRegistry = useRef<Map<number, HTMLAudioElement>>(new Map());

  const registerAudio = useCallback((trackId: number, audioElement: HTMLAudioElement) => {
    audioRegistry.current.set(trackId, audioElement);
  }, []);

  const unregisterAudio = useCallback((trackId: number) => {
    audioRegistry.current.delete(trackId);
  }, []);

  const stopAllExcept = useCallback((trackId: number) => {
    audioRegistry.current.forEach((audio, id) => {
      if (id !== trackId && !audio.paused) {
        audio.pause();
        audio.currentTime = 0;
      }
    });
  }, []);

  const stopAll = useCallback(() => {
    audioRegistry.current.forEach((audio) => {
      if (!audio.paused) {
        audio.pause();
        audio.currentTime = 0;
      }
    });
  }, []);

  return (
    <AudioManagerContext.Provider value={{ registerAudio, unregisterAudio, stopAllExcept, stopAll }}>
      {children}
    </AudioManagerContext.Provider>
  );
}

export function useAudioManager() {
  const context = useContext(AudioManagerContext);
  if (!context) {
    throw new Error("useAudioManager must be used within an AudioManagerProvider");
  }
  return context;
}

export function useAudioManagerOptional() {
  return useContext(AudioManagerContext);
}
